import React, { type MouseEvent, type KeyboardEvent, useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import {
	type CommandRowProps,
	type Command,
	CommandActionType,
	isSecondaryTrigger,
	useActionsCommandPalette,
	components,
} from '@atlassian/jira-command-palette';
import { isMacOs } from '@atlassian/jira-common-components-keyboard-shortcuts-dialog/src/utils';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouter } from '@atlassian/jira-router';
import { IconContainer, NameContainer } from '../../ui/name-container';
import { NavigationLink } from '../../ui/navigation-link';
import { NewTabHintMessage } from '../../ui/new-tab-hint-message';
import { isCurrentPage } from '../is-current-page';
import { ICONS_CONTAINER_TEST_ID, NAVIGATION_LINK_TEST_ID } from './constants';
import type {
	CommandToTransform,
	HideAndTransformNavigationCommandType,
	TransformLinkPath,
	TransformNavigationCommandType,
	UseHideCurrentPathCommandReturn,
} from './types';

export const useTransformNavigationCommands = (): UseHideCurrentPathCommandReturn => {
	const [
		{
			location: { pathname },
		},
		{ push, pushTo },
	] = useRouter();

	const { getAnalyticsAttributes } = useActionsCommandPalette();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openNewTab = useCallback((link: TransformLinkPath, openBackground?: boolean) => {
		const url = typeof link === 'string' ? link : link.path;

		const newTab = window.open(url, '_blank');

		if (openBackground) {
			newTab?.blur();
			window.focus();
		}

		return newTab;
	}, []);

	const openSameTab = useCallback(
		(link: TransformLinkPath) => {
			if (typeof link === 'string') {
				return push(link);
			}
			return pushTo(link);
		},
		[push, pushTo],
	);

	const renderCommand = useCallback(
		(props: CommandRowProps, path: TransformLinkPath, shouldAlwaysOpenNewTab?: boolean) => {
			const { onClick, ...rest } = props;
			const { command } = rest;

			const handleClick = (event: MouseEvent | KeyboardEvent) => {
				const wasSecondaryTriggered = isSecondaryTrigger(event);
				if (!wasSecondaryTriggered) {
					onClick?.(event); // Run main action as it will close the modal too
					event.preventDefault();
					return;
				}
				const analytics = getAnalyticsAttributes(
					command,
					'mouse',
					wasSecondaryTriggered,
					props?.analyticsAttributes,
				);

				const eventAnalytics = createAnalyticsEvent({});
				fireTrackAnalytics(eventAnalytics, 'commandPalette navigation', analytics);
				fireTrackAnalytics(eventAnalytics, 'commandPalette secondaryAction', {
					...analytics,
					keyPressed: isMacOs() ? 'cmdEnter' : 'ctrlEnter',
					secondaryAction: 'openNewTab',
				});
				fireTrackAnalytics(eventAnalytics, 'commandPalette taskSucceeded', analytics);
			};

			return (
				<NavigationLink
					to={path}
					onClick={handleClick}
					{...(shouldAlwaysOpenNewTab && { target: '_blank' })}
					data-testid={NAVIGATION_LINK_TEST_ID}
					tabIndex={-1}
				>
					<components.CommandRow {...rest} />
				</NavigationLink>
			);
		},
		[createAnalyticsEvent, getAnalyticsAttributes],
	);

	const transformNavigationCommand: TransformNavigationCommandType = useCallback(
		(command, { path, shouldAlwaysOpenNewTab }) => ({
			...command,
			components: {
				...command.components,
				Name: (base) => (
					<NameContainer>
						{command.components?.Name ? <command.components.Name {...base} /> : <>{command.name}</>}
						{!!shouldAlwaysOpenNewTab && (
							<IconContainer data-testid={ICONS_CONTAINER_TEST_ID}>
								<ShortcutIcon label="" />
							</IconContainer>
						)}
					</NameContainer>
				),
				CommandRow: (base) => renderCommand(base, path, shouldAlwaysOpenNewTab),
				Footer: NewTabHintMessage,
			},
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => (shouldAlwaysOpenNewTab ? openNewTab(path) : openSameTab(path)),
			},
			secondaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => openNewTab(path, true),
				preventModalClose: true,
				analyticsArgs: {
					secondaryAction: 'openNewTab',
				},
			},
		}),
		[openNewTab, openSameTab, renderCommand],
	);

	const shouldShowCommand = useCallback(
		(hideCommandPath: string | null): boolean => !isCurrentPage(hideCommandPath, pathname),
		[pathname],
	);

	const hideAndTransformNavigationCommand: HideAndTransformNavigationCommandType = useCallback(
		(commandToTransform) => {
			const { path, hideCommandPath, shouldAlwaysOpenNewTab, command } = commandToTransform;
			let hidePath = typeof path === 'string' ? path : path.path;

			if (hideCommandPath) hidePath = hideCommandPath;

			if (!shouldShowCommand(hidePath)) return undefined;

			return transformNavigationCommand(command, { path, shouldAlwaysOpenNewTab });
		},
		[shouldShowCommand, transformNavigationCommand],
	);

	const hideAndTransformNavigationCommands = useCallback(
		(commands: CommandToTransform[]): Command[] => {
			const filteredCommands: Command[] = [];

			commands.forEach((commandToTransform) => {
				const transformedCommand = hideAndTransformNavigationCommand(commandToTransform);
				if (transformedCommand) {
					filteredCommands.push(transformedCommand);
				}
			});

			return filteredCommands;
		},
		[hideAndTransformNavigationCommand],
	);

	return {
		shouldShowCommand,
		hideAndTransformNavigationCommand,
		hideAndTransformNavigationCommands,
		transformNavigationCommand,
	};
};
